import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';

import {
  PostReportParam,
  ReasonParam,
  ReportRecordParam,
  UserReportRecordParam,
  UpdatePostSkuParam,
  UpdatePostInfoImageRequest,
  UpdatePostVodThumbnailRequest,
} from '@/Interfaces/I_modal';

import {
  appendReportRecord,
  appendUserReportRecord,
  getPostDetail,
  getReasons,
  getReportRecord,
  getUserReportRecord,
  postReport,
  setPostAnnouncement,
  setPostDetail,
  setPostInfoImage,
  setPostReport,
  setReasons,
  setReportRecord,
  setUserReportRecord,
  setVodPostTag,
  updatePostAnnouncement,
  updatePostInfoImage,
  updatePostSku,
  updateVodPostTag,
  updateVodThumbnail,
  setVodThumbnail
} from '@/Slices/ModalSlice';

import {
  fetchPostDetail,
  fetchReasons,
  fetchReportRecord,
  fetchUserReportRecord,
  patchPostInfoImage,
  patchPostVodTag,
  patchPostVodThumbnail,
  postPostSkuAPI,
  postReportAPI,
} from '@/Axios/api/modal';
import { patchPostDataAPI } from '@/Axios/api/postManagement';
import { VodTag } from '@/Interfaces/I_vod';
import { uploadKocPhotoUrl } from '@/Axios/api/Media';
import { customAxios } from '@/Axios/index';

function* handlePostReport(action: PayloadAction<PostReportParam>) {
  try {
    const data: AxiosResponse = yield call(postReportAPI, action.payload);
    if (data.status === 201) {
      let content = '';
      if (action.payload.actionType === 'DISABLE') {
        content = 'The post is blocked.';
      }
      if (action.payload.actionType === 'ENABLE') {
        content = 'The post is released.';
      }
      if (action.payload.actionType === 'IGNORE') {
        content = 'The report is ignored.';
      }
      message.success({
        content,
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
      yield put(setPostReport('success'));
    } else {
      message.error({
        content: data.data?.message ?? 'Error, please try again.',
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
      yield put(setPostReport('error'));
    }
  } catch (e: any) {
    message.error({
      content: e?.response?.data?.message ?? 'Error, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(setPostReport(e));
  }
}

function* handleGetPostDetail(action: PayloadAction<string>) {
  try {
    const data: AxiosResponse = yield call(fetchPostDetail, action.payload);
    yield put(setPostDetail(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetReportRecord(action: PayloadAction<ReportRecordParam>) {
  try {
    const data: AxiosResponse = yield call(fetchReportRecord, action.payload);
    yield put(action.payload.append ? appendReportRecord(data) : setReportRecord(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetUserReportRecord(action: PayloadAction<UserReportRecordParam>) {
  try {
    const data: AxiosResponse = yield call(fetchUserReportRecord, action.payload);
    yield put(action.payload.append ? appendUserReportRecord(data) : setUserReportRecord(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetReasons(action: PayloadAction<ReasonParam>) {
  try {
    const data: AxiosResponse = yield call(fetchReasons, action.payload);
    yield put(setReasons(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleUpdatePostSku(action: PayloadAction<UpdatePostSkuParam>) {
  try {
    const data: AxiosResponse = yield call(postPostSkuAPI, action.payload);
    if (data && data.data.length > 0) {
      message.error({
        content: `Following SKU cannot be saved: ${data.data}`,
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
      yield put(getPostDetail(action.payload.post_uuid));
    } else {
      message.info({
        content: `Data has been saved`,
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
      yield put(getPostDetail(action.payload.post_uuid));
    }
  } catch (e) {
    message.error({
      content: `Cannot save: ${e}`,
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(getPostDetail(action.payload.post_uuid));
    // console.log(e);
  }
}

function* handleUpdatePostAnnouncement(
  action: PayloadAction<{ postUuid: string; announcement: string }>,
) {
  try {
    const data: AxiosResponse = yield call(patchPostDataAPI, [action.payload]);
    console.log(data);
    if (data && data.status === 200) {
      message.info({
        content: `Announcement has been updated`,
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
      yield put(setPostAnnouncement(data.data[0].announcement));
    }
  } catch (e) {
    message.error({
      content: `Fail to update announcement: ${e}`,
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(getPostDetail(action.payload.postUuid));
  }
}

function* handleUpdatePostInfoImage(action: PayloadAction<UpdatePostInfoImageRequest>) {
  try {
    const data: AxiosResponse = yield call(patchPostInfoImage, action.payload);
    yield put(setPostInfoImage(data.data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleUpdateVodPostTag(action: PayloadAction<{ uuid: string; tag?: VodTag }>) {
  try {
    const data: AxiosResponse<VodTag> = yield call(patchPostVodTag, action.payload);
    yield put(setVodPostTag(data.data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleUpdateVodThumbnail(action: PayloadAction<UpdatePostVodThumbnailRequest>) {
  try {
    const data: AxiosResponse = yield call(patchPostVodThumbnail, action.payload)
    yield put (setVodThumbnail(data.data)) 
  } catch (e) {
    // console.log(e);
  }
}

export function* watchPostReport() {
  yield takeEvery(postReport.type, handlePostReport);
}
export function* watchGetPostDetail() {
  yield takeEvery(getPostDetail.type, handleGetPostDetail);
}
export function* watchGetReportRecord() {
  yield takeEvery(getReportRecord.type, handleGetReportRecord);
}
export function* watchGetUserReportRecord() {
  yield takeEvery(getUserReportRecord.type, handleGetUserReportRecord);
}
export function* watchGetReasons() {
  yield takeEvery(getReasons.type, handleGetReasons);
}
export function* watchUpdatePostSku() {
  yield takeEvery(updatePostSku.type, handleUpdatePostSku);
}
export function* watchUpdatePostAnnouncement() {
  yield takeEvery(updatePostAnnouncement.type, handleUpdatePostAnnouncement);
}

export function* watchUpdatePostInfoImage() {
  yield takeEvery(updatePostInfoImage.type, handleUpdatePostInfoImage);
}

export function* watchUpdatePostVodTag() {
  yield takeEvery(updateVodPostTag.type, handleUpdateVodPostTag);
}

export function* watchUpdateVodThumbnail() {
  yield takeEvery(updateVodThumbnail.type, handleUpdateVodThumbnail);
}
